// Resumo executivo da order: frase-veredito + KPIs em funil do dinheiro +
// régua de saúde por dimensão. Tudo vem do motor (/payments/order/:id/analysis)
// — o frontend só apresenta; nenhum número é calculado aqui.
import {
    HEALTH_DIMENSION_LABELS as DIMENSION_LABELS,
    HEALTH_OVERALL_LABELS as OVERALL_LABELS,
    HEALTH_STATE_BADGE as STATE_BADGE,
} from '../../constants/paymentHealth';
import { RebateStyle } from '../../constants/paymentRebate';
import { FormatMoney } from '../../utils/format';
import Badge from '../ui/Badge';
import { RebateBreakdown } from './HealthCell';

const money = (v) => FormatMoney(v);

// De onde saiu o valor do rebate na frase da comissão reduzida.
const FEE_RELIEF_FONTE = {
    extrato: 'confirmado no extrato do MercadoPago',
    ml_discounts: 'declarado pelo ML para este pedido',
    payment: 'previsto pelas campanhas ativas na data',
};

// Chip neutro: rótulo que não é número nem estado — nome da campanha, título do
// anúncio. Trunca em vez de esticar o card; o texto inteiro fica no title.
function Chip({ children, title, mono = false }) {
    return (
        <span
            title={title}
            className={`inline-block max-w-full truncate rounded-full border border-mono-200 bg-mono-50 px-2 py-0.5 text-xs text-mono-600 dark:border-mono-700 dark:bg-mono-800 dark:text-mono-300 ${mono ? 'font-mono' : ''}`}
        >
            {children}
        </span>
    );
}

function KpiTile({ label, value, sub, valueClass = '', title }) {
    return (
        <div className="rounded-xl border border-mono-200 bg-white p-3 dark:border-mono-700 dark:bg-mono-900">
            <p className="text-xs text-mono-500">{label}</p>
            <p className={`truncate text-xl font-bold tabular-nums ${valueClass}`} title={title}>
                {value}
            </p>
            {sub ? <div className="mt-0.5 text-xs text-mono-400">{sub}</div> : null}
        </div>
    );
}

export function RebateChip({ rebate }) {
    if (!rebate) {
        return null;
    }
    const style = RebateStyle(rebate.state);
    const b = RebateBreakdown(rebate);
    const temNumeros = b.prometido !== null || b.recebido !== null;
    // "via cupom"/"via tarifa" por campanha só informa quando há mais de um
    // mecanismo em jogo; com um só, repete o chip do topo.
    const mostrarVia = new Set(b.campanhas.map((c) => c.via)).size > 1;
    let diferencaTexto = '—';
    if (b.diferenca !== null) {
        diferencaTexto = b.diferenca < 0 ? `−${money(-b.diferenca)}` : `+${money(b.diferenca)}`;
    }

    // Uma coisa de cada vez, sem repetir número: identidade (chips) → como o ML
    // aportou → a comissão que caiu → os três totais → quais campanhas previam.
    // Tudo que já estava dito acima sai daqui — foi o que deixou o card ilegível
    // nas versões anteriores.
    return (
        <div className="rounded-xl border border-mono-200 bg-white p-3 dark:border-mono-700 dark:bg-mono-900">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-mono-500">Rebate ML</span>
                <span className={`rounded-full border px-2 py-0.5 font-medium ${style.chip}`} title={style.hint}>
                    {style.label}
                </span>
                {b.anuncio ? (
                    <Chip mono title="anúncio conferido">
                        {b.anuncio}
                    </Chip>
                ) : null}
                {b.anuncioTitulo ? <Chip title={b.anuncioTitulo}>{b.anuncioTitulo}</Chip> : null}
            </div>
            {b.cancelado ? (
                <p className="mt-1 text-xs text-mono-500">
                    Venda desfeita ({b.motivoCancelamento}): tarifa e aporte do ML foram revertidos — nada a conferir.
                </p>
            ) : b.disputa ? (
                <p className="mt-1 text-xs text-mono-500">
                    Reclamação/mediação aberta: o veredito fica suspenso até o dinheiro assentar.
                </p>
            ) : b.motivoCobertura ? (
                <p className="mt-1 text-xs text-mono-500">Sem conferência: {b.motivoCobertura}.</p>
            ) : b.cupomNoPedido ? (
                <p className="mt-1 text-xs text-mono-500">
                    Pedido pago em {b.cupomNoPedido} pagamentos: o cupom do ML é conferido no pedido inteiro (o ML lança
                    o cupom em qualquer um deles); a tarifa, neste pagamento.
                </p>
            ) : b.mecanismoChips.length ? (
                <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-mono-500">
                    <span>Como o ML aportou:</span>
                    {b.mecanismoChips.map((m) => (
                        <Chip key={m}>{m}</Chip>
                    ))}
                    {rebate.state === 'pendente' && b.liberacaoPrevista ? (
                        <span>· liberação prevista em {b.liberacaoPrevista}</span>
                    ) : null}
                </p>
            ) : null}

            {b.feeRelief ? (
                <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
                    <b>Comissão do ML reduzida pela campanha:</b> era{' '}
                    <span className="tabular-nums line-through">{money(b.feeRelief.antes)}</span> e ficou{' '}
                    <span className="font-semibold tabular-nums">{money(b.feeRelief.depois)}</span>. A diferença de{' '}
                    <span className="font-semibold tabular-nums">{money(b.feeRelief.valor)}</span> <b>é o rebate</b>{' '}
                    desta campanha ({FEE_RELIEF_FONTE[b.feeRelief.fonte] || 'previsto pelas campanhas'}). Ele não entra
                    como dinheiro na cascata: entra como tarifa que o ML deixou de cobrar.
                </p>
            ) : null}

            {temNumeros ? (
                <div className="mt-3 grid grid-cols-3 gap-2">
                    <KpiTile
                        label="Prometido"
                        value={b.prometido === null ? '—' : money(b.prometido)}
                        sub={
                            b.prometidoDeclarado
                                ? 'valor que o ML declarou para esta venda'
                                : 'pelas campanhas ativas na data'
                        }
                    />
                    <KpiTile
                        label="Recebido"
                        value={b.recebido === null ? '—' : money(b.recebido)}
                        sub={
                            b.tarifaPendente
                                ? 'desconto na tarifa ainda não liberado'
                                : b.tarifaPelaConsulta
                                  ? 'declarado pelo ML, extrato ainda não liberou'
                                  : 'confirmado no extrato'
                        }
                    />
                    <KpiTile
                        label="Diferença"
                        value={diferencaTexto}
                        valueClass={style.text}
                        sub={b.tolerancia !== null ? `tolerância ${money(b.tolerancia)}` : null}
                    />
                </div>
            ) : null}

            {/* Com um mecanismo só, a tabela repete os três KPIs acima — o card
                é sobre o rebate, não sobre a sua própria contabilidade. */}
            {b.porMecanismo.length > 1 ? (
                <table className="mt-3 w-full text-xs tabular-nums">
                    <thead>
                        <tr className="text-left text-mono-500">
                            <th className="py-1 pr-2 font-medium">Mecanismo</th>
                            <th className="py-1 pr-2 text-right font-medium">Previsto</th>
                            <th className="py-1 pr-2 text-right font-medium">Recebido</th>
                            <th className="py-1 font-medium">Resultado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {b.porMecanismo.map((m) => (
                            <tr key={m.via} className="border-t border-mono-200 dark:border-mono-700">
                                <td className="py-1 pr-2">{m.nome}</td>
                                <td className="py-1 pr-2 text-right">
                                    {m.prometido === null ? '—' : money(m.prometido)}
                                </td>
                                <td className="py-1 pr-2 text-right">
                                    {m.recebido === null ? '—' : money(m.recebido)}
                                </td>
                                <td className={`py-1 font-medium ${RebateStyle(m.estado).text}`}>
                                    {m.label}
                                    {m.extra ? (
                                        <span className="ml-1 font-normal text-mono-500">
                                            + {money(m.extra)} de bônus do ML
                                        </span>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : null}

            {/* Só as campanhas: "O ML deu" repetia, linha a linha, o KPI
                "Recebido" logo acima, e o cupom bancado por nós não é rebate —
                este card é sobre o aporte do ML, nada mais. */}
            {temNumeros || b.campanhas.length ? (
                <div className="mt-3 text-xs">
                    <p className="mb-1 font-medium text-mono-500">As campanhas previam</p>
                    {b.campanhas.length ? (
                        <ul className="space-y-1 tabular-nums">
                            {b.campanhas.map((c) => (
                                <li
                                    key={c.id}
                                    className="flex items-start justify-between gap-3"
                                    title={
                                        c.pct && c.base !== null
                                            ? `${c.pct}% de ${money(c.base)}${c.baseKind ? ` (${c.baseKind})` : ''}`
                                            : undefined
                                    }
                                >
                                    {/* Nome e tipo em português em cima; o código da
                                        campanha fica embaixo, de apoio. */}
                                    <span className="min-w-0">
                                        <span className="flex flex-wrap items-center gap-1">
                                            {c.nome ? <Chip title={c.nome}>{c.nome}</Chip> : null}
                                            {c.tipoLabel ? <Chip title={c.tipo}>{c.tipoLabel}</Chip> : null}
                                        </span>
                                        <span className="mt-0.5 block truncate text-mono-400">
                                            {c.id}
                                            {c.pct ? ` · ${c.pct}%` : ''}
                                            {mostrarVia && c.via ? ` · via ${c.via}` : ''}
                                        </span>
                                    </span>
                                    <b className="shrink-0">{c.expected === null ? '—' : money(c.expected)}</b>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-mono-400">nenhuma campanha com aporte do ML na data</p>
                    )}
                </div>
            ) : null}
        </div>
    );
}

export function OverallBadge({ overall }) {
    const cfg = STATE_BADGE[overall] || STATE_BADGE.pending;
    return <Badge variant={cfg.variant}>{OVERALL_LABELS[overall] || overall}</Badge>;
}

export default function AnalysisSummary({ analysis }) {
    if (!analysis) {
        return null;
    }
    const { kpis, health, verdict, rules } = analysis;
    const alertCount = rules.filter((r) => ['critical', 'warn'].includes(r.severity)).length;
    const criticalCount = rules.filter((r) => r.severity === 'critical').length;

    const liquido = kpis.liquido || {};
    const liquidoValue = liquido.realizado != null ? liquido.realizado : liquido.previsto;
    const liquidoSub =
        liquido.realizado != null ? (
            <span className="text-teal-600 dark:text-teal-400">
                confirmado no extrato
                {liquido.delta != null && Math.abs(liquido.delta) > 0.01 ? (
                    <span className={liquido.delta < 0 ? 'ml-1 text-red-500' : 'ml-1 text-emerald-500'}>
                        Δ {money(liquido.delta)} vs previsto
                    </span>
                ) : null}
            </span>
        ) : liquido.previsto != null ? (
            <span>previsto (aguardando extrato)</span>
        ) : null;

    const nf = kpis.nf || {};
    const nfBadge =
        nf.status === 'AUTORIZADA'
            ? 'success'
            : nf.status === 'CANCELADA'
              ? 'danger'
              : nf.status === 'SEM_NF'
                ? 'warning'
                : 'neutral';

    const resultado = kpis.resultado || {};

    return (
        <div className="rounded-xl border border-mono-200 bg-mono-50 p-4 dark:border-mono-700 dark:bg-mono-900/60">
            <p className="mb-3 text-sm font-medium">{verdict}</p>

            <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-5">
                <KpiTile
                    label="Venda"
                    value={money(kpis.venda?.value)}
                    valueClass="text-amber-600 dark:text-amber-400"
                    title="ML: total_amount"
                    sub={
                        <span>
                            {kpis.venda?.itemsCount ? `${kpis.venda.itemsCount} item(ns)` : ''}
                            {kpis.venda?.cancelled ? <span className="ml-1 text-red-500">cancelada</span> : null}
                        </span>
                    }
                />
                <KpiTile
                    label="Líquido da venda"
                    value={money(liquidoValue)}
                    valueClass={liquido.realizado != null ? 'text-teal-600 dark:text-teal-400' : 'text-[#3483fa]'}
                    title={liquido.realizado != null ? 'Extrato: Σ linhas payment' : 'MP: net_received_amount'}
                    sub={liquidoSub}
                />
                <KpiTile
                    label="Custo da venda"
                    value={money(kpis.custo?.value)}
                    valueClass="text-red-500 dark:text-red-400"
                    title="ƒ Σ charges com accounts.from=collector − charges com accounts.to=collector (tarifas + frete + cupom seller, sem repasse de juros do comprador)"
                    sub={kpis.custo?.pct != null ? `${String(kpis.custo.pct).replace('.', ',')}% da venda` : null}
                />
                <KpiTile
                    label="Resultado do pedido"
                    value={resultado.available ? money(resultado.value) : '—'}
                    valueClass={
                        !resultado.available
                            ? 'text-mono-400'
                            : resultado.value >= 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-red-600 dark:text-red-400'
                    }
                    title="ƒ Σ créditos − débitos do extrato, excluindo reservas internas (reserve_*/payout)"
                    sub={resultado.available ? 'pós-venda incluído' : 'aguardando extrato'}
                />
                <KpiTile
                    label="NF-e"
                    value={nf.numero ? `${nf.numero}/${nf.serie}` : nf.status === 'AGUARDANDO' ? '…' : '—'}
                    valueClass="text-violet-600 dark:text-violet-400"
                    title={nf.chave || 'sem NF vinculada'}
                    sub={
                        <Badge variant={nfBadge}>
                            {nf.status === 'N/A' ? 'não aplicável' : (nf.status || '').toLowerCase()}
                        </Badge>
                    }
                />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-mono-500">Saúde:</span>
                {Object.entries(health.dimensions).map(([dim, h]) => {
                    const cfg = STATE_BADGE[h.state] || STATE_BADGE.pending;
                    return (
                        <span
                            key={dim}
                            className="inline-flex items-center gap-1"
                            title={h.rules?.length ? `regras: ${h.rules.join(', ')}` : 'sem apontamentos'}
                        >
                            <span className="text-mono-500">{DIMENSION_LABELS[dim] || dim}</span>
                            <Badge variant={cfg.variant}>{cfg.label}</Badge>
                        </span>
                    );
                })}
                {alertCount > 0 ? (
                    <a href="#alertas" className="ml-auto font-medium text-red-600 hover:underline dark:text-red-400">
                        {alertCount} alerta(s){criticalCount ? ` — ${criticalCount} crítico(s)` : ''} ↓
                    </a>
                ) : (
                    <span className="ml-auto text-mono-400">sem alertas</span>
                )}
            </div>
        </div>
    );
}
